// app.js — top-level composition for I Hear Ewe.
//
// Phase 3 assembled the core UI shell:
//   - swipeable button grid (grid.js)
//   - page indicator dots (page-navigator.js)
//   - settings panel (settings-panel.js)
//
// Phase 4 added the sound layer on top of that:
//   - splash overlay (splash.js) that captures the first user gesture
//     so we can unlock audio in Safari
//   - sound manager (sound-manager.js) resolved item id -> audio
//   - onItemTap plays the item's sound
//   - swiping to a new page triggers a best-effort preload of that
//     page's audio so it's hot in the cache by the time the child taps
//
// Phase 5 adds the tap animation layer:
//   - animation-manager.js runs the "See and Say" hero sequence
//     around each tap, awaiting the sound manager's play() promise
//     so the button stays enlarged for the entire audio sequence.
//   - A rapid second tap cancels the in-flight animation and starts
//     fresh via animation-manager.runTapAnimation() (the sound
//     manager's own interrupt is the other half of that same flow).

import { renderGrid } from './grid.js';
import { createPageNavigator } from './page-navigator.js';
import { createSettingsPanel } from './settings-panel.js';
import { createSoundManager } from './sound-manager.js';
import { createSplash } from './splash.js';
import { createAnimationManager } from './animation-manager.js';
import { getCategories } from './data/catalog.js';
import { t } from './i18n.js';
import {
  track as telemetryTrack,
  fetchConfig as telemetryFetchConfig,
  installPagehideFlush,
  destroy as telemetryDestroy,
} from './telemetry.js';

/**
 * Initialize the app inside the given root element.
 *
 * @param {HTMLElement} root
 * @param {{
 *   onItemTap?: (itemId: string, button: HTMLButtonElement) => void,
 *   soundManager?: ReturnType<typeof createSoundManager>,
 *   animationManager?: ReturnType<typeof createAnimationManager>,
 *   skipSplash?: boolean,
 *   skipTelemetry?: boolean,
 * }} [opts]
 */
export function initApp(root, opts = {}) {
  if (!(root instanceof HTMLElement)) {
    throw new TypeError('initApp: root must be an element');
  }

  root.innerHTML = '';
  root.classList.add('app-root');

  // Sound manager is injectable so tests can pass a mock backend and
  // the suite doesn't need real audio decoding.
  const soundManager = opts.soundManager ?? createSoundManager();

  // Animation manager is also injectable for tests that want to
  // observe or stub the tap-animation flow. Production uses the real
  // one which walks the DOM to apply the hero/dim classes.
  const animationManager = opts.animationManager ?? createAnimationManager();

  // --- Top chrome: title + settings button ---------------------------------
  const chrome = document.createElement('header');
  chrome.className = 'app-chrome';

  const title = document.createElement('h1');
  title.className = 'app-chrome__title';
  title.textContent = t('app.title');
  chrome.appendChild(title);

  const settingsPanel = createSettingsPanel();
  chrome.appendChild(settingsPanel.toggleButton);
  root.appendChild(chrome);
  root.appendChild(settingsPanel.panelElement);

  // --- Grid ----------------------------------------------------------------
  // Default tap handler: run the Phase 5 tap animation, and let the
  // animation manager call soundManager.play() at the appropriate
  // point in the sequence. The animation manager invokes startAudio
  // synchronously (inside the gesture stack, which matters for iOS
  // audio unlock) and passes an enterComplete gate promise through
  // to the callback. We forward that gate to soundManager.play() as
  // animalStartGate so the sound manager can hold back the ANIMAL
  // clip until the button has finished growing.
  //
  // This handles all three cases with one code path:
  //   * Verbal on, long intro: intro plays during grow, ends after
  //     grow, gate already resolved, animal plays immediately.
  //   * Verbal on, short intro (future localized voices): intro
  //     plays during grow, ends early, sound-manager blocks on the
  //     gate, animal fires the instant the button reaches max size.
  //   * Verbal off: sequence is just [animal], sound-manager blocks
  //     on the gate directly and plays animal at grow-end.
  //
  // Rapid-tap interrupts are handled by BOTH managers' token-bump
  // patterns — the stale sound-manager play() wakes up on its own
  // gate and bails at its token check.
  const onItemTap =
    opts.onItemTap ??
    ((itemId, button) => {
      telemetryTrack('item_tap', itemId);
      void animationManager.runTapAnimation(button, ({ enterComplete }) =>
        soundManager.play(itemId, { animalStartGate: enterComplete }),
      );
    });

  const grid = renderGrid(root, { onItemTap });

  // --- Page navigator (dots) -----------------------------------------------
  const nav = createPageNavigator(grid.container, grid.pageCount);
  root.appendChild(nav.dotsElement);

  // Keep the document title in sync with the current category and
  // preload the sounds for the page the child just swiped to. The
  // preload is fire-and-forget — failures are swallowed by the sound
  // manager.
  const categories = getCategories();
  const seenPanes = new Set();
  nav.onPageChange((page) => {
    const pageEl = grid.pages[page];
    const label = pageEl?.getAttribute('aria-label');
    if (label) {
      document.title = `${t('app.title')} — ${label}`;
    }
    const cat = categories[page];
    if (cat) {
      void soundManager.preloadCategory(cat.id);
      // Telemetry: pane_swipe on every navigation, pane_first_view
      // on the first visit per session.
      telemetryTrack('pane_swipe', cat.id);
      if (!seenPanes.has(cat.id)) {
        seenPanes.add(cat.id);
        telemetryTrack('pane_first_view', cat.id);
      }
    }
  });

  // The grid starts on page 0 without firing onPageChange, so the
  // initial category (barnyard) never gets tracked. Emit the same
  // pane_swipe + pane_first_view events that a real swipe would, so
  // the default landing page counts in the stats just like every
  // other category visit.
  const firstCat = categories[0];
  if (firstCat) {
    telemetryTrack('pane_swipe', firstCat.id);
    seenPanes.add(firstCat.id);
    telemetryTrack('pane_first_view', firstCat.id);
  }

  // --- Splash & audio unlock -----------------------------------------------
  // Show the tap-to-start overlay unless the caller opts out (tests do).
  // On dismiss, we unlock the audio backend on the gesture stack and
  // kick off a preload of the first category so tapping feels instant.
  let splash = null;
  if (!opts.skipSplash) {
    splash = createSplash(root, {
      onDismiss: () => {
        // unlock() must start synchronously inside the user gesture
        // for Safari to honour it. The returned promise resolves
        // asynchronously; we don't need to await it here because
        // backend.play() will implicitly wait on the unlock state.
        const unlockP = soundManager.unlock();
        // Preload the first category so the child doesn't hit cold
        // cache on their very first tap. Also fire-and-forget.
        const first = categories[0];
        if (first) {
          void soundManager.preloadCategory(first.id);
        }
        return unlockP;
      },
    });
  } else {
    // When skipping splash (tests), preload the first category up-front
    // so the test harness can assert preload behavior without needing
    // to simulate a gesture.
    const first = categories[0];
    if (first) {
      void soundManager.preloadCategory(first.id);
    }
  }

  // --- Telemetry (Phase 8) -------------------------------------------------
  // Fire-and-forget config fetch so the telemetry module picks up
  // server-side batching/retry params. Then record a page_load event
  // and install the pagehide listener for the final flush. Tests can
  // skip all of this with skipTelemetry so the suite doesn't need to
  // mock fetch or worry about sendBeacon.
  let removePagehideFlush = null;
  if (!opts.skipTelemetry) {
    void telemetryFetchConfig();
    telemetryTrack('page_load');
    removePagehideFlush = installPagehideFlush();
  }

  // --- Visibility change (background/foreground) ---------------------------
  // When the user switches away from the app (e.g. answering a phone call
  // on iOS), stop any playing sound and snap the tap animation back to its
  // rest state immediately. Without this the hero button stays zoomed until
  // the HOLD_MAX_MS safety timeout fires, because the AudioContext becomes
  // suspended and the play() promise hangs.
  function handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      soundManager.stop();
      animationManager.cancelCurrent();
    }
  }
  document.addEventListener('visibilitychange', handleVisibilityChange);

  return {
    grid,
    nav,
    settingsPanel,
    soundManager,
    animationManager,
    splash,
    destroy() {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (removePagehideFlush) removePagehideFlush();
      telemetryDestroy();
      nav.destroy();
      settingsPanel.destroy();
      soundManager.destroy();
      animationManager.destroy();
      root.innerHTML = '';
      root.classList.remove('app-root');
    },
  };
}
